// The REAL backend OPG trades skill client (CR-20260716-001 WB-4; ADR-033/ADR-037, contract C-3).
//
// Implements the IOpgTradesSkill seam over the family transport: send RunOpgCoPilotSkill (the
// HTTP call returns an ack), receive OpgCoPilotTradesReadyDto over the ESS WebSocket, correlate
// by runId. The DI-provided instance is MODULE-LEVEL — ONE client serves ALL OPG panel
// instances — so in-flight runs live in a pending-run MAP keyed by runId, never a single-flight
// slot (each panel's own single-flight is the session's job, not this adapter's).
//
// Transport rules (ADR-033):
//   - SUBSCRIBE BEFORE SEND: the push rides the ESS WebSocket while the command ack is a
//     separate HTTP call — subscribing first removes the "push beat the ack" race.
//   - GUARANTEED TEARDOWN: every pending entry carries a hard teardown timer (the orchestrator's
//     45s backstop + a small grace). Every settle path — resolve, each reject, the timer, an ack
//     failure, even processCommand throwing synchronously — funnels through ONE choke point that
//     unsubscribes, kills the timer and clears the map entry, so no promise or subscription can
//     outlive its run under ANY outcome (superseded-then-silent donor semantics included).
//   - Outcome mapping (C-3): succeeded/empty -> resolve with per-side { trades }; superseded ->
//     reject with a DISTINGUISHABLE reason (today's orchestrator maps any reject to
//     terminalFailed — accepted v1, noted in ADR-033); terminalFailed/preSendBlocked -> reject.
//     Late/foreign runId pushes are ignored.
//
// COURIER (§6): the questionnaire crosses with SEMANTIC fidelity. The only transport-layer
// mapping is OpgDateSelection's JS Date members -> 'yyyy-MM-dd' calendar strings from LOCAL
// date parts (getFullYear/getMonth/getDate — NEVER toISOString, which converts to UTC and can
// shift a picked calendar date across midnight), plus hoisting panelInstanceId out of the seam
// context to the op's top level (C-1). Result trades map onto OpgTradeContent as camelCase
// plain interfaces — no class-transformer; backend nulls for optional members normalize to
// ABSENT so domain consumers see the exact pre-backend shapes.
//
// ES5 note: arrays + indexOf only (no Set/Map iteration, no for..of), matching the sibling
// copilot logic; the pending map is a plain object keyed by runId.

import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {getShortUUID, isValidNumber} from '../utils';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {
    isOverrideComplete,
    MarketConditionOverride,
    OpgAtmOffsetSub,
    OpgAtmRangeValue,
    OpgDateSelection,
    OpgMaxRisk,
    OpgPriceRange,
    OpgSide,
    OpgTradeContent,
    SideInputs
} from './opg-copilot-domain';
import {IOpgTradesSkill, OpgSkillContext, OpgSkillRequest, OpgSkillResult, OpgSkillSideResult} from './opg-trades-skill';
import {OPG_COPILOT_RUN_BACKSTOP_MS} from './opg-copilot-session';
import {
    OPG_COPILOT_CONTRACT_VERSION,
    OPG_COPILOT_OUTCOME_EMPTY,
    OPG_COPILOT_OUTCOME_PRE_SEND_BLOCKED,
    OPG_COPILOT_OUTCOME_SUCCEEDED,
    OPG_COPILOT_OUTCOME_SUPERSEDED,
    OPG_COPILOT_OUTCOME_TERMINAL_FAILED,
    OPG_COPILOT_TRADES_READY_MESSAGE_TYPE,
    OpgCoPilotAtmOffsetSubDto,
    OpgCoPilotAtmRangeValueDto,
    OpgCoPilotContextDto,
    OpgCoPilotExpectedRangeModelDto,
    OpgCoPilotLegDto,
    OpgCoPilotMarketOverrideDto,
    OpgCoPilotMaxRiskDto,
    OpgCoPilotPriceRangeDto,
    OpgCoPilotSideInputsDto,
    OpgCoPilotTradeDto,
    OpgCoPilotTradesReadyDto,
    OpgEsContractCandidateDto,
    OpgEsContractGapDto,
    RunOpgCoPilotSkill
} from './opg-copilot-operations';
import {PricingGridLegDescriptor} from '../options-pricing-grid/model/pricing-grid-leg.descriptor';
import {StrategyTemplate} from '../options-pricing-grid/model/strategy.template';

// Hard per-run teardown deadline = the orchestrator's 45s run backstop (the ONE authoritative
// constant, OPG_COPILOT_RUN_BACKSTOP_MS in opg-copilot-session.ts — referenced, never a second
// hardcoded 45) + a small grace. The grace guarantees the ORCHESTRATOR's backstop always fires
// first, so the user-visible "took too long" settlement is the session's; this timer is purely
// the resource reaper for runs nothing will ever push to (donor-silent supersede, dead Shell).
export const OPG_SKILL_TEARDOWN_GRACE_MS = 5000;
export const OPG_SKILL_TEARDOWN_MS = OPG_COPILOT_RUN_BACKSTOP_MS + OPG_SKILL_TEARDOWN_GRACE_MS;

// ---- Distinguishable rejection (C-3) -------------------------------------------------------
// The superseded rejection must be tellable apart from a terminal failure. ES5 target: Error
// subclassing mis-sets the prototype chain, so the marker is a tagged plain Error + type guards.

const REJECTION_OUTCOME_FIELD = 'opgSkillOutcome';

export function makeSkillRejection(outcome: string, message: string): Error {
    const e = new Error(message);
    (e as any)[REJECTION_OUTCOME_FIELD] = outcome;
    return e;
}

// The C-2 outcome (or FE-minted equivalent) a skill rejection carries; null for foreign errors.
export function skillRejectionOutcomeOf(e: any): string | null {
    return (e && typeof e[REJECTION_OUTCOME_FIELD] === 'string') ? e[REJECTION_OUTCOME_FIELD] : null;
}

export function isSupersededRejection(e: any): boolean {
    return skillRejectionOutcomeOf(e) === OPG_COPILOT_OUTCOME_SUPERSEDED;
}

// ---- The client -----------------------------------------------------------------------------

interface PendingRun {
    subscription: Subscription;
    timerHandle: any;
    resolve: (result: OpgSkillResult) => void;
    reject: (error: Error) => void;
}

@Injectable()
export class BackendOpgTradesSkill implements IOpgTradesSkill {

    constructor(
        private readonly _messageBus: MessageBusService,
        private readonly _shellClient: ShellClientService
    ) {}

    // Pending-run MAP keyed by (client-minted) runId. Plain object — ES5-safe, and the karma
    // leak assertions read pendingRunCount after every settle path (KAR-06/08).
    private readonly _pending: { [runId: string]: PendingRun } = {};

    get pendingRunCount(): number {
        return Object.keys(this._pending).length;
    }

    run(request: OpgSkillRequest): Promise<OpgSkillResult> {
        return new Promise<OpgSkillResult>((resolve, reject) => {
            // Client-side short-circuit (KAR-10): an empty underlying is a request the backend
            // pre-send validator would deterministically block anyway (ADR-037) — fail it here
            // without a round-trip, a subscription or a map entry. Transport-level guard on a
            // transport-added field, NOT questionnaire judgment (courier stays intact).
            const underlying = (request && request.context) ? request.context.underlying : '';
            if (!underlying) {
                reject(makeSkillRejection(OPG_COPILOT_OUTCOME_PRE_SEND_BLOCKED, 'Underlying Symbol is Missing.'));
                return;
            }

            // The WIRE runId (echoed by the push). Minted HERE, per run, with the family id
            // convention (getShortUUID — the same mint the session uses for its own run ids):
            // the seam signature run(request) is frozen (C-3), so the orchestrator's session
            // run id never reaches this adapter and cannot be reused.
            const runId = getShortUUID();

            // (1) SUBSCRIBE BEFORE SEND (ADR-033). Per-run subscription; foreign runIds fall
            // through the correlation check in onTradesReady.
            const subscription = this._messageBus
                .of<OpgCoPilotTradesReadyDto>(OPG_COPILOT_TRADES_READY_MESSAGE_TYPE)
                .subscribe(m => this.onTradesReady(runId, m ? m.payload : null));

            // (2) hard teardown timer — the reaper for runs nothing will ever push to.
            const timerHandle = setTimeout(() => {
                this.settle(runId, entry => entry.reject(makeSkillRejection(
                    OPG_COPILOT_OUTCOME_TERMINAL_FAILED,
                    'The ORION Run Received no Result Within the Teardown Deadline.')));
            }, OPG_SKILL_TEARDOWN_MS);

            this._pending[runId] = {subscription, timerHandle, resolve, reject};

            // (3) send. The ack failing (rejected promise) or processCommand throwing
            // SYNCHRONOUSLY (e.g. invalid session) both settle through the choke point —
            // no path leaves the entry behind.
            try {
                this._shellClient
                    .processCommand(buildRunCommand(runId, request))
                    .catch((e: any) => {
                        const message = (e && e.message) ? String(e.message) : 'The ORION Run Could Not be Sent.';
                        this.settle(runId, entry => entry.reject(
                            makeSkillRejection(OPG_COPILOT_OUTCOME_TERMINAL_FAILED, message)));
                    });
            } catch (e) {
                const message = (e && e.message) ? String(e.message) : 'The ORION Run Could Not be Sent.';
                this.settle(runId, entry => entry.reject(
                    makeSkillRejection(OPG_COPILOT_OUTCOME_TERMINAL_FAILED, message)));
            }
        });
    }

    // ESS push handler for ONE pending run: correlate on runId (late/foreign pushes ignored —
    // the entry stays untouched for its own push/timer), then map the outcome per C-3.
    private onTradesReady(runId: string, dto: OpgCoPilotTradesReadyDto | null): void {
        if (!dto || dto.runId !== runId) {
            return;   // not this run's push
        }
        if (dto.outcome === OPG_COPILOT_OUTCOME_SUCCEEDED || dto.outcome === OPG_COPILOT_OUTCOME_EMPTY) {
            const result = toSkillResult(dto);
            this.settle(runId, entry => entry.resolve(result));
            return;
        }
        // superseded (distinguishable via the outcome tag) / terminalFailed / preSendBlocked —
        // and any future unknown outcome fails CLOSED as a rejection carrying that outcome.
        const message = dto.failureDetail
            || (dto.outcome === OPG_COPILOT_OUTCOME_SUPERSEDED
                ? 'Superseded by a Newer Run From the Same Panel.'
                : 'The ORION Run Failed.');
        this.settle(runId, entry => entry.reject(makeSkillRejection(dto.outcome, message)));
    }

    // THE teardown choke point (ADR-033 "guaranteed teardown"): remove the map entry, close the
    // subscription, kill the timer — THEN settle the promise. Every path funnels here, so a
    // second settle for the same runId finds no entry and is a no-op (idempotent), and no
    // promise/subscription/timer can leak on any outcome.
    private settle(runId: string, complete: (entry: PendingRun) => void): void {
        const entry = this._pending[runId];
        if (!entry) {
            return;   // already settled (or never registered)
        }
        delete this._pending[runId];
        entry.subscription.unsubscribe();
        clearTimeout(entry.timerHandle);
        complete(entry);
    }
}

// ---- Request mapping (domain -> wire), module-level pure functions --------------------------

function pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
}

// 'yyyy-MM-dd' from LOCAL calendar components — TZ-drift-safe (see file header / C-1 date rule).
function toCalendarDateString(d: Date): string {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

interface WireDateFields {
    dateKind: string;
    date: string | null;
    rangeStart: string | null;
    rangeEnd: string | null;
}

// Exactly one variant per dateKind, the other fields null (C-1). 'loaded' never reaches this
// adapter in a healthy flow (the orchestrator resolves it to 'single' at Run); if it ever does,
// the kind passes through VERBATIM and the backend pre-send validator blocks it
// deterministically (preSendBlocked) — the adapter never invents a date.
function mapDateFields(sel: OpgDateSelection): WireDateFields {
    if (sel && sel.kind === 'single') {
        return {
            dateKind: 'single',
            date: sel.date instanceof Date ? toCalendarDateString(sel.date) : null,
            rangeStart: null,
            rangeEnd: null
        };
    }
    if (sel && sel.kind === 'range') {
        return {
            dateKind: 'range',
            date: null,
            rangeStart: sel.start instanceof Date ? toCalendarDateString(sel.start) : null,
            rangeEnd: sel.end instanceof Date ? toCalendarDateString(sel.end) : null
        };
    }
    return {dateKind: sel ? sel.kind : '', date: null, rangeStart: null, rangeEnd: null};
}

// Every bounded kind branches BEFORE the atmOffset tail — the family's recorded silent-misclone
// hazard (CR-20260715-001 lesson) — mirroring the orchestrator's cloneSideInputs discipline.
// CR-20260727-002: no `direction` on any branch — the field is off the wire (contract v4); the
// backend derives the coverage direction from the side it is normalizing.
function mapPriceRange(pr: OpgPriceRange): OpgCoPilotPriceRangeDto {
    if (pr.kind === 'strikes') {
        return {kind: 'strikes', startStrike: pr.startStrike, endStrike: pr.endStrike, anchor: null, offset: null, sub: null};
    }
    if (pr.kind === 'followThrough') {
        return {kind: 'followThrough', startStrike: pr.startStrike, endStrike: null, anchor: null, offset: null, sub: null};
    }
    if (pr.kind === 'expectedRange') {
        // The fact + the start strike, nothing else (the skill computes the per-date end).
        return {kind: 'expectedRange', startStrike: pr.startStrike, endStrike: null, anchor: null, offset: null, sub: null};
    }
    return {kind: 'atmOffset', startStrike: null, endStrike: null, anchor: pr.anchor, offset: pr.offset, sub: mapAtmSub(pr.sub)};
}

function mapAtmSub(sub: OpgAtmOffsetSub): OpgCoPilotAtmOffsetSubDto {
    if (sub && sub.kind === 'range') {
        return {kind: 'range', value: mapAtmRangeValue(sub.value)};
    }
    if (sub && sub.kind === 'both') {
        return {kind: 'both', value: mapAtmRangeValue(sub.value)};
    }
    return {kind: 'followThrough', value: null};
}

function mapAtmRangeValue(v: OpgAtmRangeValue): OpgCoPilotAtmRangeValueDto {
    if (v && v.mode === 'dollar') {
        return {mode: 'dollar', extent: v.extent};
    }
    return {mode: 'expectedRange', extent: null};
}

// ---- Normalized -> plain-dollar conversion (contract v5, CR-20260827-002) -------------------
//
// THE ONE PLACE the Default Qty still means anything to a Select run. The wire carries PLAIN
// DOLLARS only — the owner's ratified formula: Raw ($) = Normalized × $100 × Default Qty — and
// the engine computes per-contract (its multiplier is an absolute contract count), exactly as
// the Adjust engine has since 2026-08-13. The questionnaire keeps its `Normalized (Def. Qty)`
// typing mode as a DISPLAY convenience; this seam folds the qty in before sending.
//
// DUPLICATED from hedging-grid/copilot/copilot-mapper.ts on purpose (isolated-sibling rule —
// the copilot families share no feature code; see opg-copilot-pricing.ts header).

const OPG_CONTRACT_MULTIPLIER = 100;

function usableQty(defaultQty: number): boolean {
    return typeof defaultQty === 'number' && isFinite(defaultQty) && defaultQty > 0;
}

// null = REFUSAL sentinel: a normalized entry with no usable Default Qty cannot be converted,
// and sending a guess would run the engine on a number nobody typed. The backend refuses a
// null-amount dollar cap / non-positive min profit loudly (MalformedSideInputs), which is the
// correct surface for it.
function toPlainDollars(amount: number, normalized: boolean, defaultQty: number): number | null {
    if (!normalized) {
        return amount;
    }
    return usableQty(defaultQty) ? amount * defaultQty * OPG_CONTRACT_MULTIPLIER : null;
}

// Non-nullable flavour for the min-profit field: the refusal sentinel is 0, which the
// normalizer rejects by name ("does not derive a positive W").
function toPlainDollarsOrZero(amount: number, normalized: boolean, defaultQty: number): number {
    const dollars = toPlainDollars(amount, normalized, defaultQty);
    return dollars === null ? 0 : dollars;
}

// null passes through as null — the ABSENCE of the risk question is part of the contract
// (hide-risk-for-follow-through; the backend side-inputs DTO is nullable for exactly this).
function mapMaxRisk(mr: OpgMaxRisk | null, defaultQty: number): OpgCoPilotMaxRiskDto | null {
    if (!mr) {
        return null;
    }
    if (mr.kind === 'dollar') {
        // Contract v5: plain dollars, always — the wire flag is gone.
        return {kind: 'dollar', amount: toPlainDollars(mr.amount, mr.normalized, defaultQty), level: null};
    }
    return {kind: 'relative', amount: null, level: mr.level};
}

function mapSideInputs(side: SideInputs | undefined, defaultQty: number): OpgCoPilotSideInputsDto | null {
    if (!side) {
        return null;   // the tab's other side — null on the wire (C-1)
    }
    if (side.mode === 'preset') {
        return {mode: 'preset', presetId: side.presetId, presetAnchor: side.presetAnchor, presetApgSource: side.presetApgSource,
            presetQuantity: defaultQty, priceRange: null, minProfitDollars: 0, maxRisk: null};
    }
    return {
        priceRange: mapPriceRange(side.priceRange),
        minProfitDollars: toPlainDollarsOrZero(
            side.minProfit.amount, side.minProfit.normalized, defaultQty),
        maxRisk: mapMaxRisk(side.maxRisk, defaultQty)
    };
}

// Seam context -> wire context: field-for-field EXCEPT panelInstanceId, which the adapter
// HOISTS to the op's top level (C-1 — the backend context DTO deliberately has no such member),
// and defaultQty, which since contract v5 does NOT cross at all — it is consumed here as the
// normalized->dollars conversion input (see toPlainDollars above) and stays a seam-level field.
function mapContext(ctx: OpgSkillContext): OpgCoPilotContextDto {
    return {
        underlying: ctx.underlying,
        expirations: ctx.expirations,
        selectedExpiration: ctx.selectedExpiration,
        strikeLadder: ctx.strikeLadder,
        chosenAtm: ctx.chosenAtm,
        dynamicAtm: ctx.dynamicAtm
    };
}

// Seam override map -> wire array (CR-20260722-002). Two filters:
//   - only EDITED dates cross. A seeded-but-untouched date is being DISPLAYED, not overridden
//     (owner ruling D6); shipping it would make every date the user merely looked at override.
//   - only COMPLETE records cross (D8/D22), judged by the SAME predicate the Run button is gated
//     on.
//
// That shared predicate is the point. This filter used to be the only place completeness was
// checked, and the only thing it can do about a bad record is discard it — so an invalid override
// produced a SUCCESSFUL run that silently measured the market instead (owner-reported
// 2026-07-22). Dropping is not enforcement. `canRun` now refuses the run first, and this stays
// only as a backstop that cannot fire in practice.
// Key order is not significant — the backend indexes by date and never iterates.
function mapMarketOverrides(
    overrides: { [dateKey: string]: MarketConditionOverride } | undefined,
    needsCallSkew: boolean,
    needsPutSkew: boolean
): OpgCoPilotMarketOverrideDto[] | null {
    if (!overrides || (!needsCallSkew && !needsPutSkew)) {
        return null;
    }
    const out: OpgCoPilotMarketOverrideDto[] = [];
    const keys = Object.keys(overrides);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const o = overrides[key];
        // Same consume-scoped predicate the Run button is gated on (the 2026-08-04 ruling).
        if (!o || !o.edited || !isOverrideComplete(o, needsCallSkew, needsPutSkew)) {
            continue;
        }
        out.push({date: key, ivCall: needsCallSkew ? o.ivCall : null, ivPut: needsPutSkew ? o.ivPut : null,
            vsCall: needsCallSkew ? o.vsCall : null, vsPut: needsPutSkew ? o.vsPut : null,
            hsCall: needsCallSkew ? o.hsCall : null, hsPut: needsPutSkew ? o.hsPut : null});
    }
    return out.length > 0 ? out : null;
}

function buildRunCommand(runId: string, request: OpgSkillRequest): RunOpgCoPilotSkill {
    const dateFields = mapDateFields(request.date);
    const defaultQty = request.context.defaultQty;
    return new RunOpgCoPilotSkill(
        OPG_COPILOT_CONTRACT_VERSION,
        runId,
        request.context.panelInstanceId,   // hoisted out of the seam context (C-1)
        request.tab,
        dateFields.dateKind,
        dateFields.date,
        dateFields.rangeStart,
        dateFields.rangeEnd,
        mapSideInputs(request.sides.calls, defaultQty),
        mapSideInputs(request.sides.puts, defaultQty),
        mapContext(request.context),
        mapMarketOverrides(request.marketOverrides, !!request.sides.calls && request.sides.calls.mode !== 'preset',
            !!request.sides.puts && request.sides.puts.mode !== 'preset'),
        mapExpectedRangeModel(request.expectedRangeModel),
        // CR-20260924-002 (ES mode, Hedge Matrix host only): courier, verbatim.
        request.context.esMode === true,
        mapEsContracts(request.context.esMode === true ? request.context.esContracts : null)
    );
}

function mapEsContracts(candidates: OpgEsContractCandidateDto[] | null | undefined): OpgEsContractCandidateDto[] | null {
    if (!candidates || candidates.length === 0) {
        return null;
    }
    return candidates.map(c => ({contract: c.contract, gap: isValidNumber(c.gap) ? c.gap : null}));
}

// CR-20260727-001. The user's expected-move MODEL, verbatim. Null (absent selection, or the
// setting unusable) means "ATM Straddle" server-side, which is byte-for-byte contract-v2 behaviour.
//
// The model strings are the CONTRACT on both sides — there is no enum anywhere in this chain, and
// the backend switches on these exact literals — so they cross unmapped and must not be re-cased.
// customPercent is a MULTIPLIER of the straddle (0.01-5.0, shown as 1%-500% by the settings box's
// "0#%" format), never a percent of the underlying; it crosses unconverted too.
function mapExpectedRangeModel(
    selection: { model: string; customPercent: number | null } | null | undefined
): OpgCoPilotExpectedRangeModelDto | null {
    if (!selection || !selection.model) {
        return null;
    }
    return {
        model: selection.model,
        customPercent: isValidNumber(selection.customPercent) ? selection.customPercent : null
    };
}

// ---- Result mapping (wire -> seam), module-level pure functions ------------------------------

// A side is present iff the backend sent trades for it — a null/empty wire array is that side's
// legitimate ABSENCE (the seam's "nothing to suggest"), exactly how the orchestrator's
// toSideContents treats it downstream. Wire arrays can arrive null (backend nullable members) —
// never assume [].
function toSkillResult(dto: OpgCoPilotTradesReadyDto): OpgSkillResult {
    const result: OpgSkillResult = {};
    // CR-20260924-002: on an ES-mode run every trade names its contract and the push carries the
    // gap the lens used per contract; each trade remembers its own so the card and its preview
    // land on the right chain and the right silo whatever the toggle says later.
    const esGaps = toEsGapMap(dto.esContracts);
    const calls = toSideResult(dto.calls, esGaps);
    if (calls) {
        result.calls = calls;
    }
    const puts = toSideResult(dto.puts, esGaps);
    if (puts) {
        result.puts = puts;
    }
    // CR-20260902-001: empty-side explanations, only meaningful for a side WITHOUT trades.
    if (!calls && dto.callsNote) {
        result.callsNote = dto.callsNote;
    }
    if (!puts && dto.putsNote) {
        result.putsNote = dto.putsNote;
    }
    return result;
}

function toSideResult(trades: OpgCoPilotTradeDto[] | null, esGaps: { [contract: string]: number }): OpgSkillSideResult | null {
    if (!trades || trades.length === 0) {
        return null;
    }
    return {trades: trades.map(t => toTradeContent(t, esGaps))};
}

function toEsGapMap(gaps: OpgEsContractGapDto[] | null | undefined): { [contract: string]: number } {
    const out: { [contract: string]: number } = {};
    (gaps || []).forEach(g => {
        if (g && g.contract && isValidNumber(g.gap)) {
            out[g.contract] = g.gap;
        }
    });
    return out;
}

// Wire trade -> OpgTradeContent, field-for-field (C-2 mirrors the domain type). camelCase plain
// objects, no class-transformer. Backend nulls for OPTIONAL domain members normalize to ABSENT
// (tradeType: null on single-family runs; nullable leg members) so domain consumers see exactly
// the shapes they saw pre-backend. Score is the backend's display-only fake ranking companion
// (M2 smoke ruling 2026-07-16) — mapped through when present, absent otherwise.
function toTradeContent(t: OpgCoPilotTradeDto, esGaps: { [contract: string]: number }): OpgTradeContent {
    const content: OpgTradeContent = {
        presetId: t.presetId, presetRevision: t.presetRevision, presetName: t.presetName, presetProvenance: t.presetProvenance,
        name: t.name,
        side: t.side as OpgSide,
        expiration: t.expiration,
        startingStrike: t.startingStrike,
        mold: t.mold as StrategyTemplate,   // frozen DV-2 enumeration — backend values are union members
        legs: (t.legs || []).map(toLegDescriptor),
        width: t.width,
        priceAtSelection: t.priceAtSelection,
        maxRiskLabel: t.maxRiskLabel || ''
    };
    if (t.tradeType === 'range' || t.tradeType === 'followThrough') {
        content.tradeType = t.tradeType;
    }
    if (typeof t.score === 'number') {
        content.score = t.score;
    }
    if (t.esContract) {
        content.esContract = t.esContract;
        content.esGap = esGaps.hasOwnProperty(t.esContract) ? esGaps[t.esContract] : null;
    }
    return content;
}

function toLegDescriptor(l: OpgCoPilotLegDto): PricingGridLegDescriptor {
    const leg: PricingGridLegDescriptor = {type: l.type as 'leg' | 'width'};
    if (l.side === 'Buy' || l.side === 'Sell') {
        leg.side = l.side;
    }
    if (typeof l.qty === 'number') {
        leg.qty = l.qty;
    }
    if (typeof l.width === 'number') {
        leg.width = l.width;
    }
    if (typeof l.expirationOffset === 'number') {
        leg.expirationOffset = l.expirationOffset;
    }
    return leg;
}
